import {
  Badge,
  DataTable,
  DescriptionList,
  Modal,
  type BadgeColor,
  type DataTableColumn,
  type DataTableFooterRow,
  type DataTableRow,
  type DescriptionListItem,
} from '@enamelai/design-system'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { AppointmentStatus } from '@dataTypes/appointment.type'
import { BillingProcedure, LedgerEntry } from '@dataTypes/billing.types'
import {
  ClaimEntry,
  ClaimProcedure,
  TransactionEntry,
} from '@services/api/billing/transaction.api'
import { formatCurrency } from '@utils/billing.utils'
import { mapClaimStatus } from '@utils/claims.util'
import React, { useMemo } from 'react'

interface Props {
  isVisible: boolean
  closeModal: () => void
  transaction: TransactionEntry | null
}

const CLAIM_STATUS_LABELS: Record<string, string> = {
  approved: StatusTypes.Approved,
  rejected: StatusTypes.Rejected,
  'under review': StatusTypes.UnderReview,
  'partially approved': StatusTypes.PartiallyApproved,
  cancelled: StatusTypes.Cancelled,
  denied: StatusTypes.Denied,
  pending: StatusTypes.Pending,
}

const BILLING_STATUS_LABELS: Record<string, string> = {
  paid: StatusTypes.Paid,
  'partially paid': StatusTypes.PartiallyPaid,
  unpaid: StatusTypes.Unpaid,
  unbilled: StatusTypes.Unbilled,
}

const CLAIM_COLUMNS: DataTableColumn[] = [
  { id: 'procedure_code', label: 'Code', width: '80px' },
  { id: 'procedure_date', label: 'Procedure date', width: '130px' },
  { id: 'clinic_fee', label: 'Clinic fee', width: '110px' },
  { id: 'allowed_amount', label: 'Allowed amt.', width: '110px' },
  { id: 'write_off', label: 'Write-off', width: '110px' },
  { id: 'estimated_insurance_pay', label: 'Est. ins. pay', width: '110px' },
  { id: 'approved_amount', label: 'Approved ins.', width: '115px' },
  { id: 'deductible', label: 'Deductible', width: '110px' },
  { id: 'copay', label: 'Copay', width: '110px' },
  { id: 'estimated_patient_resp', label: 'Patient res.', width: '110px' },
]

const PROCEDURE_COLUMNS: DataTableColumn[] = [
  { id: 'procedureCode', label: 'Code', width: '110px' },
  { id: 'clinicFee', label: 'Clinic fee', width: '110px' },
  { id: 'allowedAmount', label: 'Allowed amt.', width: '110px' },
  { id: 'writeOff', label: 'Write-off', width: '110px' },
  { id: 'estimatedInsurance', label: 'Estimate ins.', width: '110px' },
  { id: 'approvedInsurance', label: 'Approved ins.', width: '110px' },
  { id: 'insuranceAdjustment', label: 'Insurance adj.', width: '110px' },
  { id: 'discount', label: 'Discount', width: '110px' },
  { id: 'patientResponsibility', label: 'Patient resp.', width: '110px' },
  { id: 'patientAdjustment', label: 'Patient adj.', width: '110px' },
]

const postingBadgeColor = (status: string): BadgeColor => {
  switch (status) {
    case StatusTypes.Approved:
    case StatusTypes.Cleared:
    case StatusTypes.Paid:
    case StatusTypes.Confirmed:
    case StatusTypes.CheckIn:
      return 'vivid-green'
    case StatusTypes.Rejected:
    case StatusTypes.Denied:
    case StatusTypes.Cancelled:
    case StatusTypes.Unpaid:
    case StatusTypes.Unbilled:
    case StatusTypes.Alert:
    case StatusTypes.Late:
      return 'red'
    case StatusTypes.UnderReview:
    case StatusTypes.Pending:
    case StatusTypes.PartiallyPaid:
    case StatusTypes.NoShow:
    case StatusTypes.Unconfirmed:
      return 'yellow'
    case StatusTypes.PartiallyApproved:
      return 'pale-green'
    case StatusTypes.Deposited:
      return 'orange'
    case StatusTypes.Completed:
    case StatusTypes.FollowUp:
    case StatusTypes.InTreatment:
    case StatusTypes.Draft:
      return 'blue'
    case StatusTypes.NewPatient:
    case StatusTypes.CheckOut:
      return 'teal'
    default:
      return 'gray'
  }
}

const statusBadge = (status: string): React.ReactElement => (
  <Badge mode="tinted" color={postingBadgeColor(status)} label={status} />
)

const displayText = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === '' ? 'N/A' : String(value)

const displayDate = (value: string | null | undefined): string => {
  if (typeof value === 'string' && value) {
    return new Date(value).toLocaleDateString()
  }
  return 'N/A'
}

const displayOptionalCurrency = (value: number | null | undefined): string =>
  typeof value === 'number' ? formatCurrency(value) : 'N/A'

const mappedStatusLabel = (
  value: string | null | undefined,
  labels: Record<string, string>,
): string | null => {
  if (!value) return null
  return labels[value.trim().toLowerCase()] ?? value
}

const claimStatusLabel = (value: string | null | undefined): string | null => {
  if (!value) return null
  const mapped = CLAIM_STATUS_LABELS[mapClaimStatus(value).status.toLowerCase()]
  if (mapped) return mapped
  const statusLower = value.toLowerCase()
  if (statusLower.includes('approved') && statusLower.includes('partial')) {
    return StatusTypes.PartiallyApproved
  }
  if (statusLower.includes('approved')) return StatusTypes.Approved
  if (statusLower.includes('rejected')) return StatusTypes.Rejected
  if (statusLower.includes('under review')) return StatusTypes.UnderReview
  if (statusLower.includes('partially')) return StatusTypes.PartiallyApproved
  if (statusLower.includes('cancelled')) return StatusTypes.Cancelled
  if (statusLower.includes('denied')) return StatusTypes.Denied
  if (statusLower.includes('pending')) return StatusTypes.Pending
  return StatusTypes.Pending
}

const appointmentClaimStatusLabel = (
  value: string | null | undefined,
): string | null => {
  if (!value) return null
  const mapped = CLAIM_STATUS_LABELS[mapClaimStatus(value).status.toLowerCase()]
  if (mapped) return mapped
  return mappedStatusLabel(value, CLAIM_STATUS_LABELS)
}

const checkStatusLabel = (status: string): string => {
  const statusLower = status.toLowerCase()
  if (statusLower.includes('cleared')) return StatusTypes.Cleared
  if (statusLower.includes('pending')) return StatusTypes.Pending
  return StatusTypes.Pending
}

const statusValue = (status: string | null): React.ReactNode =>
  status ? statusBadge(status) : 'N/A'

const appointmentStatusLabel = (appointmentStatus: AppointmentStatus): string =>
  StatusTypes[
    AppointmentStatus[appointmentStatus] as keyof typeof StatusTypes
  ] ?? ''

const currencyOrZero = (value: number | null | undefined): string =>
  formatCurrency(value ?? 0)

const claimGroupsFor = (claimEntry: ClaimEntry): DescriptionListItem[][] => {
  const claimStatus = claimStatusLabel(claimEntry.claimStatus)
  const rawCheckStatus = claimEntry.checkStatus
  const checkValue =
    !rawCheckStatus || rawCheckStatus === 'N/A'
      ? 'N/A'
      : statusBadge(checkStatusLabel(rawCheckStatus))

  return [
    [
      { label: 'Patient name', value: claimEntry.patientName },
      { label: 'Payer', value: claimEntry.payerCarrier },
      { label: 'Subscriber', value: claimEntry.subscriber },
      { label: 'Provider', value: claimEntry.provider },
      { label: 'Entity type', value: claimEntry.entityType },
    ],
    [
      { label: 'Claim ID', value: claimEntry.claimId },
      { label: 'Claim status', value: statusValue(claimStatus) },
      {
        label: 'Claim amount',
        value: `$${claimEntry.submittedAmount.toString()}`,
      },
      { label: 'Item type', value: claimEntry.itemType },
      { label: 'Claim submitted', value: claimEntry.dateSent },
    ],
    [
      { label: 'Check/EFT/Ref no.', value: claimEntry.checkNo },
      { label: 'Status', value: checkValue },
      { label: 'Paid amount', value: claimEntry.paidAmount || '0.00' },
      { label: 'Mode of payment', value: claimEntry.modeOfPayment },
      { label: 'Payment date', value: claimEntry.paymentDate || 'N/A' },
    ],
  ]
}

const appointmentGroupsFor = (entry: LedgerEntry): DescriptionListItem[][] => [
  [
    { label: 'Patient name', value: entry.patientName },
    { label: 'Appointment type', value: entry.appointmentType },
    { label: 'Appointment date', value: displayDate(entry.appointmentDate) },
    {
      label: 'Appointment status',
      value: statusValue(
        appointmentStatusLabel(entry.appointmentStatus) || null,
      ),
    },
  ],
  [
    { label: 'Provider', value: displayText(entry.providerName) },
    { label: 'Claim ID', value: displayText(entry.claimId) },
    { label: 'Claim submitted', value: displayText(entry.claimSubmitted) },
    {
      label: 'Claim status',
      value: statusValue(appointmentClaimStatusLabel(entry.claimStatus)),
    },
  ],
  [
    { label: 'Paid by', value: displayText(entry.paidBy) },
    { label: 'Payment method', value: displayText(entry.paymentMethod) },
    { label: 'Payment date', value: displayDate(entry.paymentDate) },
    {
      label: 'Billing status',
      value: statusValue(
        mappedStatusLabel(entry.status, BILLING_STATUS_LABELS),
      ),
    },
  ],
  [
    {
      label: 'Insurance adj. type',
      value: displayText(entry.insuranceAdjustmentType),
    },
    {
      label: 'Patient adj. type',
      value: displayText(entry.patientAdjustmentType),
    },
    {
      label: 'Appointment adjustment',
      value: displayOptionalCurrency(entry.appointmentAdjustment),
    },
    {
      label: 'Appointment balance',
      value: displayOptionalCurrency(entry.appointmentBalance),
    },
  ],
]

const toClaimRow = (
  procedure: ClaimProcedure,
  index: number,
): DataTableRow => ({
  id: `${procedure.procedure_code}-${procedure.procedure_date}-${index}`,
  procedure_code: procedure.procedure_code,
  procedure_date: procedure.procedure_date,
  clinic_fee: currencyOrZero(procedure.clinic_fee),
  allowed_amount: currencyOrZero(procedure.allowed_amount),
  write_off: currencyOrZero(procedure.write_off),
  estimated_insurance_pay: currencyOrZero(procedure.estimated_insurance_pay),
  approved_amount: currencyOrZero(procedure.approved_amount),
  deductible: currencyOrZero(procedure.deductible),
  copay: currencyOrZero(procedure.copay),
  estimated_patient_resp: currencyOrZero(procedure.estimated_patient_resp),
})

const toProcedureRow = (procedure: BillingProcedure): DataTableRow => ({
  id: procedure.procedureLogId,
  procedureCode: procedure.procedureCode,
  clinicFee: currencyOrZero(procedure.clinicFee),
  allowedAmount: currencyOrZero(procedure.allowedAmount),
  writeOff: currencyOrZero(procedure.writeOff),
  estimatedInsurance: currencyOrZero(procedure.estimatedInsurance),
  approvedInsurance: currencyOrZero(procedure.approvedInsurance),
  insuranceAdjustment: currencyOrZero(procedure.insuranceAdjustment),
  discount: currencyOrZero(procedure.discount),
  patientResponsibility: currencyOrZero(procedure.patientResponsibility),
  patientAdjustment: currencyOrZero(procedure.patientAdjustment),
})

const ViewTransactionInfo: React.FC<Props> = ({
  isVisible,
  closeModal,
  transaction,
}) => {
  const isClaimTransaction = transaction?.type === 'Claim'
  const isAppointmentTransaction =
    transaction?.type === 'Procedure' || transaction?.type === 'Billing'

  const claimProcedures = useMemo(
    () =>
      isClaimTransaction && transaction?.claimEntry
        ? transaction.claimEntry.procedures
        : [],
    [isClaimTransaction, transaction],
  )

  const procedureTableData = useMemo(
    () =>
      isAppointmentTransaction && transaction?.ledgerEntry
        ? transaction.ledgerEntry.procedures || []
        : [],
    [isAppointmentTransaction, transaction],
  )

  const claimRows = useMemo(
    () => claimProcedures.map(toClaimRow),
    [claimProcedures],
  )

  const procedureRows = useMemo(
    () => procedureTableData.map(toProcedureRow),
    [procedureTableData],
  )

  const claimFooterRow = useMemo((): DataTableFooterRow => {
    const totals = claimProcedures.reduce(
      (acc, row) => ({
        clinic_fee: acc.clinic_fee + (row.clinic_fee || 0),
        allowed_amount: acc.allowed_amount + (row.allowed_amount || 0),
        write_off: acc.write_off + (row.write_off || 0),
        estimated_insurance_pay:
          acc.estimated_insurance_pay + (row.estimated_insurance_pay || 0),
        approved_amount: acc.approved_amount + (row.approved_amount || 0),
        deductible: acc.deductible + (row.deductible || 0),
        copay: acc.copay + (row.copay || 0),
        estimated_patient_resp:
          acc.estimated_patient_resp + (row.estimated_patient_resp || 0),
      }),
      {
        clinic_fee: 0,
        allowed_amount: 0,
        write_off: 0,
        estimated_insurance_pay: 0,
        approved_amount: 0,
        deductible: 0,
        copay: 0,
        estimated_patient_resp: 0,
      },
    )

    return {
      procedure_code: 'Total',
      clinic_fee: formatCurrency(totals.clinic_fee),
      allowed_amount: formatCurrency(totals.allowed_amount),
      write_off: formatCurrency(totals.write_off),
      estimated_insurance_pay: formatCurrency(totals.estimated_insurance_pay),
      approved_amount: formatCurrency(totals.approved_amount),
      deductible: formatCurrency(totals.deductible),
      copay: formatCurrency(totals.copay),
      estimated_patient_resp: formatCurrency(totals.estimated_patient_resp),
    }
  }, [claimProcedures])

  const procedureFooterRow = useMemo((): DataTableFooterRow => {
    const totals = procedureTableData.reduce(
      (acc, proc) => ({
        clinicFee: acc.clinicFee + (proc.clinicFee || 0),
        allowedAmount: acc.allowedAmount + (proc.allowedAmount || 0),
        writeOff: acc.writeOff + (proc.writeOff || 0),
        estimatedInsurance:
          acc.estimatedInsurance + (proc.estimatedInsurance || 0),
        approvedInsurance:
          acc.approvedInsurance + (proc.approvedInsurance || 0),
        insuranceAdjustment:
          acc.insuranceAdjustment + (proc.insuranceAdjustment || 0),
        discount: acc.discount + (proc.discount || 0),
        patientResponsibility:
          acc.patientResponsibility + (proc.patientResponsibility || 0),
        patientAdjustment:
          acc.patientAdjustment + (proc.patientAdjustment || 0),
      }),
      {
        clinicFee: 0,
        allowedAmount: 0,
        writeOff: 0,
        estimatedInsurance: 0,
        approvedInsurance: 0,
        insuranceAdjustment: 0,
        discount: 0,
        patientResponsibility: 0,
        patientAdjustment: 0,
      },
    )

    return {
      procedureCode: 'Total',
      clinicFee: formatCurrency(totals.clinicFee),
      allowedAmount: formatCurrency(totals.allowedAmount),
      writeOff: formatCurrency(totals.writeOff),
      estimatedInsurance: formatCurrency(totals.estimatedInsurance),
      approvedInsurance: formatCurrency(totals.approvedInsurance),
      insuranceAdjustment: formatCurrency(totals.insuranceAdjustment),
      discount: formatCurrency(totals.discount),
      patientResponsibility: formatCurrency(totals.patientResponsibility),
      patientAdjustment: formatCurrency(totals.patientAdjustment),
    }
  }, [procedureTableData])

  if (!transaction) {
    return null
  }

  const title = isClaimTransaction
    ? 'View claim'
    : isAppointmentTransaction
      ? 'View appointment'
      : 'Transaction info'

  return (
    <Modal
      title={title}
      open={isVisible}
      onClose={closeModal}
      size="xl"
      closeOnBackdrop={false}
      primaryLabel=""
      secondaryLabel="Close"
      onSecondaryClick={closeModal}
      style={{ maxWidth: 'min(1600px, calc(100vw - 48px))' }}
    >
      {isClaimTransaction && transaction.claimEntry ? (
        <div className="mb-2">
          <DescriptionList
            groups={claimGroupsFor(transaction.claimEntry)}
            variant="panel"
          />
        </div>
      ) : null}
      {isAppointmentTransaction && transaction.ledgerEntry ? (
        <div className="mb-2">
          <DescriptionList
            groups={appointmentGroupsFor(transaction.ledgerEntry)}
            variant="panel"
          />
        </div>
      ) : null}
      {isClaimTransaction ? (
        <DataTable
          columns={CLAIM_COLUMNS}
          rows={claimRows}
          footerRow={claimFooterRow}
          showActions={false}
        />
      ) : null}
      {isAppointmentTransaction ? (
        <DataTable
          columns={PROCEDURE_COLUMNS}
          rows={procedureRows}
          footerRow={procedureFooterRow}
          showActions={false}
        />
      ) : null}
    </Modal>
  )
}

export default ViewTransactionInfo
